import React from "react";

import { t } from "../i18n";
/** Status pill colours and labels - same as the merchant app's StatusColors (`status.*` strings). */
const STATUS = {
  CREATED: ["#efecf6", "#5e5646", "status.created"],
  READY: ["#e6eef6", "#1f4e79", "status.ready"],
  PLANNED: ["#eee9f8", "#6b4eaa", "status.planned"],
  DRAFT: ["#eee9f8", "#6b4eaa", "status.draft"],
  CONFIRMED: ["#e2daf4", "#3f2d6b", "status.confirmed"],
  BROADCASTED: ["#f4ecdd", "#7a5a1f", "status.broadcasted"],
  ASSIGNED: ["#faf1df", "#8a6420", "status.assigned"],
  PICKED_UP: ["#fbe9d7", "#94490f", "status.picked_up"],
  DISPATCHED: ["#fbe9d7", "#94490f", "status.dispatched"],
  DELIVERED: ["#e6f2ec", "#2f7a57", "status.delivered"],
  COMPLETED: ["#e6f2ec", "#2f7a57", "status.completed"],
  CANCELLED: ["#f8e5e3", "#a63a3a", "status.cancelled"],
};

/** Order statuses offered as filters on Orders / Fulfill (app: kMerchantOrdersScreenStatusFiltersExtended). */
export const ORDER_STATUS_FILTERS = [
  "CREATED",
  "READY",
  "PLANNED",
  "CONFIRMED",
  "BROADCASTED",
  "PICKED_UP",
  "DELIVERED",
  "CANCELLED",
  "ASSIGNED",
  "COMPLETED",
];

export function normalizeStatus(raw) {
  return String(raw || "").trim().toUpperCase().replace(/ /g, "_");
}

export function statusLabel(raw) {
  const n = normalizeStatus(raw);
  if (!n) return "-";
  if (STATUS[n]) return t(STATUS[n][2]);
  const words = n.toLowerCase().split("_");
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(" ");
}

export function statusColors(raw) {
  const s = STATUS[normalizeStatus(raw)];
  return s ? { bg: s[0], fg: s[1] } : { bg: "#f0eef6", fg: "#55505e" };
}

export function StatusPill({ status }) {
  const { bg, fg } = statusColors(status);
  return (
    <span className="status-pill" style={{ background: bg, color: fg }}>
      {statusLabel(status)}
    </span>
  );
}

/**
 * Filter chips with count badges; plum when selected (app: MerchantOrderStatusFilterBar / FilterPill).
 * `options`: [{ id, label, count, dot? }]. Options with a zero count are hidden unless selected.
 */
export function FilterBar({ options, value, onChange, ariaLabel }) {
  return (
    <div className="filter-bar" role="tablist" aria-label={ariaLabel || t("common.filter")}>
      {options
        .filter((o) => o.id === "all" || o.count > 0 || o.id === value)
        .map((o) => {
          const selected = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`filter-pill${selected ? " filter-pill--selected" : ""}`}
              onClick={() => onChange(o.id)}
            >
              {o.dot && !selected ? <span className="filter-pill__dot" style={{ background: o.dot }} /> : null}
              <span>{o.label}</span>
              <span className="filter-pill__count">{o.count}</span>
            </button>
          );
        })}
    </div>
  );
}

/** Build status filter options (All + each status) with counts from a list of orders. */
export function statusFilterOptions(orders, statuses = ORDER_STATUS_FILTERS) {
  const counts = {};
  for (const o of orders || []) {
    const s = normalizeStatus(o.status);
    counts[s] = (counts[s] || 0) + 1;
  }
  return [
    { id: "all", label: t("common.all"), count: (orders || []).length },
    ...statuses.map((s) => ({ id: s, label: statusLabel(s), count: counts[s] || 0, dot: statusColors(s).fg })),
  ];
}

export function matchesStatusFilter(order, filter) {
  return filter === "all" || normalizeStatus(order.status) === filter;
}

/** Rounded search box with a magnifier and a clear (×) button - same look on every list page. */
export function SearchField({ value, onChange, placeholder, ariaLabel }) {
  return (
    <div className="search-field">
      <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
      <input
        type="search"
        placeholder={placeholder || t("common.search_placeholder")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel || t("common.search")}
      />
      {value ? (
        <button type="button" className="search-field__clear" onClick={() => onChange("")} aria-label={t("common.clear_search")}>
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** Order search: order #, receiver phone, delivery address or any product name / SKU on the order. */
export function orderMatchesSearch(order, q, lineLabel = (i) => i?.productName || i?.sku || "") {
  const raw = String(q || "").trim();
  if (!raw) return true;
  const needle = raw.toLowerCase().replace(/^#/, "");
  if (String(order.id).includes(needle)) return true;
  const digits = needle.replace(/\D/g, "");
  if (digits && String(order.clientPhone || "").includes(digits)) return true;
  if (String(order.deliveryAddress || "").toLowerCase().includes(needle)) return true;
  return (order.items || []).some(
    (i) =>
      String(lineLabel(i) || "").toLowerCase().includes(needle) || String(i?.sku || "").toLowerCase().includes(needle)
  );
}

/** Page title block: gold eyebrow, serif title, lead text and right-aligned actions. */
export function PageHeader({ eyebrow, title, lead, actions }) {
  return (
    <div className="page-header">
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1 className="page-header__title">{title}</h1>
        {lead ? <p className="page-header__lead">{lead}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </div>
  );
}

/** "Today, 14:05" / "Yesterday, 09:12" / "12 Sep, 18:40"- like the app's formatWhen. */
export function formatWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (days === 0) return t("common.today_at", { time });
  if (days === 1) return t("common.yesterday_at", { time });
  const date = d.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${date}, ${time}`;
}

export function EmptyState({ icon = "fa-receipt", title, message, action }) {
  return (
    <div className="empty-state">
      <i className={`fa-solid ${icon} empty-state-icon`} aria-hidden="true" />
      <div style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--ink)", marginBottom: 6 }}>{title}</div>
      {message ? <p style={{ margin: "0 auto 16px", maxWidth: 420 }}>{message}</p> : null}
      {action}
    </div>
  );
}

/** Digits of a receiver phone for `tel:` links (stored as 2519XXXXXXXX). */
function phoneDigits(phone) {
  return String(phone || "").replace(/\D/g, "");
}

/** `tel:` href and display text for a receiver phone, or null when there is no number. */
export function phoneParts(phone) {
  const d = phoneDigits(phone);
  if (!d) return null;
  const intl = d.startsWith("251");
  return { href: intl ? `tel:+${d}` : `tel:${d}`, text: intl ? `+${d}` : d };
}

/** Tappable receiver phone; doesn't trigger the surrounding row's click. */
export function PhoneLink({ phone, style }) {
  const p = phoneParts(phone);
  if (!p) return null;
  return (
    <a className="phone-link" href={p.href} onClick={(e) => e.stopPropagation()} style={style}>
      <i className="fa-solid fa-phone" aria-hidden="true" /> {p.text}
    </a>
  );
}

/** Same link as {@link PhoneLink} for Leaflet popup HTML (digits only, so safe to inline). */
export function phoneLinkHtml(phone) {
  const p = phoneParts(phone);
  return p ? `<a class="phone-link" href="${p.href}">${p.text}</a>` : "";
}
